import { Button } from 'components/ui/Button'
import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Text,
  Textarea,
} from '@chakra-ui/core'
import useLocaleText from 'components/useLocaleText'
import { Reservations } from 'constants/interfaces'
import { ReasonsList, ReasonItem } from './ReasonComponents'
import { ReasonConfig } from 'components/TasksV2/UI/ReasonComponents/reasonsConfig'

interface SkipReasonsModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reservations: Reservations) => void
  predefinedReasons?: ReasonConfig[]
  isSubmitting?: boolean
  hasError?: boolean
}

export const SkipReasonsModal: React.FC<SkipReasonsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  predefinedReasons = [],
  isSubmitting = false,
  hasError = false,
}) => {
  const [reasons, setReasons] = useState<ReasonItem[]>([])
  const [customReason, setCustomReason] = useState('')

  // Translations
  const t_skip_title = useLocaleText('skip_with_reasons', true)
  const t_select_reasons = useLocaleText('select_skip_reason', true)
  const t_cancel = useLocaleText('cancel', true)
  const t_confirm = useLocaleText('save', true)
  const t_enter_reason = useLocaleText('add_reason', true)

  const hasPredefinedReasons = predefinedReasons.length > 0

  const toggleReason = (key: string) => {
    if (isSubmitting) return
    setReasons((prev) => prev.map((r) => (r.key === key ? { ...r, isSelected: !r.isSelected } : r)))
  }

  const handleConfirm = () => {
    if (isSubmitting) return

    let reasonsArray: string[] = []

    if (hasPredefinedReasons) {
      // Use selected predefined reasons
      reasonsArray = reasons.filter((r) => r.isSelected).map((r) => r.key)
    } else {
      // Use custom reason from textarea
      if (customReason.trim()) {
        reasonsArray = [customReason.trim()]
      }
    }

    const reservations: Reservations = {
      reasons: reasonsArray,
      score_deduction: 0, // Always 0 for skip
    }

    onConfirm(reservations)
  }

  const isValid = hasPredefinedReasons ? reasons.some((r) => r.isSelected) : customReason.trim().length > 0

  // Initialize reasons
  useEffect(() => {
    if (!isOpen) return

    if (hasPredefinedReasons) {
      const initialReasons = predefinedReasons.map((r) => ({
        key: r.key,
        isSelected: false,
      }))
      setReasons(initialReasons)
    }
    setCustomReason('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={!isSubmitting}
      size="lg"
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.5)" zIndex={1500} />
      <ModalContent maxW="550px" borderRadius="12px" overflow="hidden" maxH="90vh" zIndex={1501}>
        <ModalHeader py="16px" px="24px" bg="#FEF3C7" borderBottom="1px solid" borderColor="#FCD34D">
          <Flex align="center" style={{ gap: '12px' }}>
            {/*<SkipForward size={24} color="#D97706" />*/}
            <Text fontSize="18px" fontWeight="600" color="#D97706">
              {t_skip_title}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody py="24px" px="24px" bg="#FFFFFF" overflowY="auto">
          <Flex direction="column" style={{ gap: '16px' }}>
            {hasPredefinedReasons ? (
              /* Show predefined reasons list */
              <ReasonsList
                title={t_select_reasons}
                reasons={reasons}
                onToggle={toggleReason}
                variant="warning"
                showScore={false}
              />
            ) : (
              /* Show simple textarea for custom reason */
              <>
                <Textarea
                  isDisabled={isSubmitting}
                  value={customReason}
                  onChange={(e: any) => setCustomReason(e.target.value)}
                  placeholder={t_enter_reason || t_select_reasons}
                  height="100px"
                  resize="none"
                  borderRadius="8px"
                  borderColor="#E5E7EB"
                  _focus={{ borderColor: '#D97706', boxShadow: '0 0 0 1px #D97706' }}
                  fontSize="14px"
                />
              </>
            )}
          </Flex>
        </ModalBody>

        {hasError && (
          <Text role="alert" color="#bb5651" px="24px" pb="16px">
            Could not skip this subtask. Please try again.
          </Text>
        )}

        <ModalFooter py="16px" px="24px" borderTop="1px solid #E5E7EB" bg="#F9FAFB" justifyContent="space-between">
          <Button
            type="button"
            onClick={onClose}
            isDisabled={isSubmitting}
            variant="ghost"
            color="#6B7280"
            fontSize="14px"
            fontWeight="500"
            _hover={{ bg: '#F3F4F6', color: '#374151' }}
          >
            {t_cancel}
          </Button>

          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            bg="#D97706"
            color="#FFFFFF"
            fontSize="14px"
            fontWeight="600"
            px="24px"
            py="10px"
            borderRadius="6px"
            isDisabled={!isValid || isSubmitting}
            isLoading={isSubmitting}
            _hover={{
              bg: '#B45309',
              transform: 'translateY(-1px)',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            _disabled={{
              bg: '#D1D5DB',
              cursor: 'not-allowed',
              opacity: 0.6,
            }}
            transition="all 0.2s"
          >
            {t_confirm}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
